import React, { useState, useEffect } from 'react';
import Icon from '../../Components/Icons/Icon';
import { classNames, match } from '../../Services/helpers';
import { useScroll, scrollDirection } from '../../Components/Help';
import { useAssistantContext } from '../../Components/Assistant';
import Menu from './Menu';
import { useSession } from '../../Components/Session';
import GlobalSearch from '../../Components/GlobalSearch';
import { levels } from '../../Data/Admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMobile } from '../../Components/Mobile';
import useHomeBadge from '../../Data/useHomeBadge';
import { useOffline } from '../../Components/Offline';
import Status from '../../Components/Offline/Status';

const transition = 'transition-all duration-200';
const wrapper = 'flex flex-col items-center justify-between gap-2';
const wrapperCompact = 'p-1';
const wrapperOpen = 'p-2 pb-1';
const wrapperStatus = 'p-2 pb-0';

const NavRedesign = () => {
    const path = useLocation().pathname;
    const [hidden, setHidden] = useState(false);
    const [compact, setCompact] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
    const {isAdmin, isAdminSwitched, adminSwitchBack, showDarkMode} = useSession();
    const {isNavHidden} = useMobile();
    const [active, setActive] = useState('home');
    const navigate = useNavigate();
    const homeBadge = useHomeBadge();
    const {status} = useOffline();

    useScroll((direction) => {
        setCompact(direction === scrollDirection.DOWN);
    });

    // Hide nav bar when keyboard is open
    useEffect(() => {
        if (hidden !== isNavHidden) {
            setHidden(isNavHidden);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNavHidden, path]);

    const handleClick = (e, handler) => {
        e.preventDefault();
        e.stopPropagation();

        if (compact) {
            setCompact(false);
        } else {
            handler();
        }
    }

    useEffect(() => {
        const path = window.location.pathname.split('/')[1];
        setActive(match(path, {
            '': 'home',
            'reports': 'reports',
            'admin': 'admin',
            'default': 'other',
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname]);

    const wrapperExtra = compact ? wrapperCompact : (status ? wrapperStatus : wrapperOpen);

    return <>
        <div className="fixed bottom-3 left-0 right-0 z-20 bg-transparent flex justify-center pointer-events-none">
            <div className={classNames(
                    transition,
                    'inline-flex m-auto rounded-full overflow-hidden',
                    'bg-slate-50/70 dark:bg-gray-800/50 dark:text-slate-100 dark:fill-slate-100 backdrop-blur-md',
                    'flex-col items-center',
                    'border dark:border-slate-600',
                    // compact ? 'border border-white/50 dark:border-black/50' : 'border-3 border-white dark:border-black',
                    compact ? 'mb-0' : 'mb-2',
                    compact ? 'min-w-0' : 'min-w-[90%]',
                    hidden ? 'inert opacity-0 scale-0' : 'scale-100',
                )}
                style={{
                    boxShadow: showDarkMode ?
                        (compact ? '0px 2px 5px 0px rgba(0,0,0,0.5)' : '0px 0px 10px 0px rgba(0,0,0,0.5)') :
                        (compact ? '0px 2px 5px 0px rgba(0,0,0,0.2)' : '0px 0px 10px 0px rgba(0,0,0,0.2)')
                }}
            >
                <div className={classNames(
                    'w-full flex items-center',
                    'justify-around',
                    hidden ? 'pointer-events-none' : 'pointer-events-auto',
                    compact ? 'px-2 pt-2' : 'px-5 py-1',
                )}>

                    <div className={classNames(transition, wrapper, wrapperExtra)}
                            onClick={(e) => handleClick(e, () => setMenuOpen(!menuOpen))}
                        >
                        <Icon icon="Bars"
                            size={compact ? '1x' : '2x'}
                            className={transition}
                        />
                        <Title title="Tools" hide={compact || !!status} />
                    </div>
                    <div className={classNames(transition, wrapper, wrapperExtra)}
                            onClick={(e) => handleClick(e, () => setGlobalSearchOpen(!globalSearchOpen))}
                        >
                        <Icon icon="MagnifyingGlass"
                            size={compact ? '1x' : '2x'}
                            className={transition}
                        />
                        <Title title="Search" hide={compact || !!status} />
                    </div>
                    <div className={classNames(transition, wrapper, wrapperExtra)}
                            onClick={(e) => handleClick(e, () => navigate('/'))}
                        >
                        <Icon icon="HouseChimney"
                            size={compact ? '1x' : '2x'}
                            badge={homeBadge ? true : null}
                            theme={active === 'home' ? 'solid' : 'light'}
                            className={classNames(transition, active === 'home' && 'fill-brand')}
                        />
                        <Title title="Home" hide={compact || !!status} />
                    </div>
                    {/*
                    <div className={classNames(transition, wrapper, wrapperExtra)}
                            onClick={(e) => handleClick(e, () => navigate('/reports'))}
                        >
                        <Icon icon="ChartSimple"
                            size={compact ? '1x' : '2x'}
                            theme={active === 'reports' ? 'solid' : 'light'}
                            className={classNames(transition, active === 'reports' && 'fill-brand')}
                        />
                        <Title title="Reports" hide={compact || !!status} />
                    </div>
                    */}
                    {isAdmin(levels.SUPER) && <div className={classNames(transition, wrapper, wrapperExtra)}
                            onClick={(e) => handleClick(e, () => navigate('/admin'))}
                        >
                        <Icon icon="Wrench"
                            size={compact ? '1x' : '2x'}
                            theme={active === 'admin' ? 'solid' : 'light'}
                            className={classNames(transition, active === 'admin' && 'fill-brand')}
                        />
                        <Title title="Admin" hide={compact || !!status} />
                    </div>}
                    {isAdminSwitched && <div className={classNames(transition, wrapper, wrapperExtra, 'fill-red-600')}
                            onClick={(e) => handleClick(e, () => adminSwitchBack())}
                        >
                        <Icon icon="ArrowLeftToBracket" size={compact ? '1x' : '2x'} className={transition} />
                        <Title title="Back" hide={compact || !!status} />
                    </div>}
                    <Assistant {...{compact, hideTitle: compact || !!status, wrapperExtra}} />
                </div>
                <Status className={classNames(
                    'text-center text-xs transition-all duration-200 w-full mx-auto',
                    (compact || status === null) ? 'h-0 py-0 w-0' : 'h-6 py-1 w-full',
                )} />
            </div>
        </div>
        <Menu isOpen={menuOpen} setIsOpen={() => setMenuOpen(false)} />
        <GlobalSearch isOpen={globalSearchOpen} setIsOpen={() => setGlobalSearchOpen(false)} />
    </>
}

const Title = ({ title, hide }) => {
    return <span className={classNames(
        'text-muted text-xs',
        'transition-all duration-200 overflow-hidden',
        hide ? 'max-h-0 max-w-0 opacity-0' : 'max-h-4 max-w-12 opacity-100',
    )}>
        {title}
    </span>
}

const Assistant = ({ compact, hideTitle, wrapperExtra }) => {
    const { assistantOpen, setAssistantOpen, unseen} = useAssistantContext();
    const {showDarkMode} = useSession();

    return <div className={classNames(transition, wrapper, wrapperExtra)}
            onClick={() => setAssistantOpen(!assistantOpen)}
        >
        <Icon icon={
                assistantOpen || unseen ? (showDarkMode ? "SidSolidDark" : "SidSolid") : (showDarkMode ? "SidLightDark" : "SidLight")
            }
            theme="soapy"
            size={compact ? '1x' : '2x'}
            className={transition}
        />
        <Title title="Sid" hide={hideTitle || !!status} />
    </div>
}

export default NavRedesign;
